/**
 * runidea.ai — Phase 13 — BRRRR Financials tab.
 *
 * Specialised Financials view for `deal_type === "brrrr"`. Renders the
 * 3-phase pipeline: Acquire+Rehab → Refinance → Hold (rental on new loan).
 * Highlights the Infinite-Return flag when total cash recycled ≥ total
 * cash in.
 */
import { useFmt } from "@/lib/useFmt";
import { Wrench, Repeat, Home, Sparkles } from "lucide-react";

function Row({ label, value, indent = false, bold = false, divider = false, tone = "neutral" }) {
  const cls = {
    neutral: "text-slate-900",
    good: "text-emerald-700",
    bad: "text-red-600",
    muted: "text-slate-500",
  }[tone];
  return (
    <tr className={divider ? "border-t border-slate-200" : ""}>
      <td className={`py-2.5 pr-4 text-sm ${indent ? "pl-8 text-slate-500" : "pl-0 text-slate-700"} ${bold ? "font-semibold text-slate-900" : ""}`}>
        {label}
      </td>
      <td className={`py-2.5 pl-4 text-right text-sm tabular-nums ${bold ? "font-bold" : ""} ${cls}`}>
        {value}
      </td>
    </tr>
  );
}

export default function BRRRRFinancialsTab({ deal }) {
  const fmt = useFmt();
  const infinite = !!deal.infinite_return_flag;
  const recycledPct = deal.capital_recycled_pct;
  const cashLeftTone = (deal.cash_left_in_deal || 0) <= 0.01 ? "good" : "neutral";
  const cfTone = (deal.cash_flow_monthly || 0) >= 0 ? "good" : "bad";

  return (
    <div className="space-y-6" data-testid="brrrr-financials-tab">
      {/* Hero strip */}
      {infinite && (
        <div
          data-testid="brrrr-infinite-return-banner"
          className="flex items-center gap-3 rounded-xl border-2 border-purple-300 bg-gradient-to-r from-purple-50 to-fuchsia-50 px-5 py-4 shadow-sm"
        >
          <div className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-purple-600 text-white">
            <Sparkles className="h-5 w-5" />
          </div>
          <div>
            <p className="text-base font-bold text-purple-900">Infinite return detected</p>
            <p className="text-xs text-purple-700">
              You&apos;ll pull more cash out at refinance than you put in — every dollar of cash flow is infinite ROI.
            </p>
          </div>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-xl border border-purple-200 bg-purple-50 p-5 shadow-sm" data-testid="brrrr-kpi-recycled">
          <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-purple-700">
            <Repeat className="h-3 w-3" /> Capital Recycled
          </div>
          <div className="mt-2 text-2xl font-bold tabular-nums text-slate-900">
            {recycledPct != null ? `${recycledPct.toFixed(1)}%` : "—"}
          </div>
          <div className="mt-1 text-xs text-slate-500">{fmt.money(deal.cash_recycled)} pulled out</div>
        </div>
        <div className={`rounded-xl border p-5 shadow-sm ${cashLeftTone === "good" ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-white"}`} data-testid="brrrr-kpi-left">
          <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">Cash Left in Deal</div>
          <div className={`mt-2 text-2xl font-bold tabular-nums ${cashLeftTone === "good" ? "text-emerald-700" : "text-slate-900"}`}>
            {fmt.money(deal.cash_left_in_deal)}
          </div>
          <div className="mt-1 text-xs text-slate-500">After cash-out refi</div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="brrrr-kpi-postcoc">
          <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">Post-Refi CoC</div>
          <div className="mt-2 text-2xl font-bold tabular-nums text-slate-900">
            {deal.post_refi_cash_on_cash_pct != null
              ? `${deal.post_refi_cash_on_cash_pct.toFixed(1)}%`
              : infinite ? "∞" : "—"}
          </div>
          <div className="mt-1 text-xs text-slate-500">on cash left in deal</div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="brrrr-kpi-cf">
          <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">
            <Home className="h-3 w-3" /> Hold Cash Flow
          </div>
          <div className={`mt-2 text-2xl font-bold tabular-nums ${cfTone === "good" ? "text-emerald-700" : "text-red-600"}`}>
            {fmt.money(deal.cash_flow_monthly, { cents: true })} / mo
          </div>
          <div className="mt-1 text-xs text-slate-500">{fmt.money(deal.cash_flow_annual)} / yr</div>
        </div>
      </div>

      {/* Phase 1 — Acquire + Rehab */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-purple-100 text-xs font-bold text-purple-700">1</span>
          <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500">Acquire &amp; Rehab</h3>
        </div>
        <table className="mt-4 w-full">
          <tbody>
            <Row label="Purchase price" value={fmt.money(deal.purchase_price)} indent />
            <Row label="Repair budget" value={fmt.money(deal.repair_budget)} indent />
            <Row label="Initial loan" value={fmt.money(deal.loan_amount)} indent />
            <Row label="Down payment" value={fmt.money(deal.down_payment_amount)} indent />
            <Row label="Closing costs" value={fmt.money(deal.closing_costs)} indent />
            <Row label="Holding costs" value={fmt.money(deal.holding_costs_total)} indent />
            <Row label="Interest during stabilisation" value={fmt.money(deal.interest_during_stabilization)} indent />
            <Row label="Total cash in" value={fmt.money(deal.total_cash_in)} indent bold />
          </tbody>
        </table>
      </div>

      {/* Phase 2 — Refinance */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-purple-100 text-xs font-bold text-purple-700">2</span>
          <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500">Refinance</h3>
        </div>
        <table className="mt-4 w-full">
          <tbody>
            <Row label="ARV target" value={fmt.money(deal.arv)} indent />
            <Row label={`New loan @ ${deal.refi_ltv_pct ?? 0}% LTV`} value={fmt.money(deal.new_loan)} indent />
            <Row label="Refi closing costs" value={`− ${fmt.money(deal.refi_closing_costs, { cents: true })}`} indent tone="bad" />
            <Row label="Initial loan payoff" value={`− ${fmt.money(deal.loan_amount, { cents: true })}`} indent tone="bad" />
            <Row label="Cash pulled out" value={fmt.money(deal.cash_recycled)} indent bold tone="good" />
            <Row label="Cash left in deal" value={fmt.money(deal.cash_left_in_deal)} indent bold tone={cashLeftTone} />
          </tbody>
        </table>
      </div>

      {/* Phase 3 — Hold (rental on new loan) */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-purple-100 text-xs font-bold text-purple-700">3</span>
          <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500">Hold</h3>
        </div>
        <table className="mt-4 w-full">
          <tbody>
            <Row label="Stabilised rent (monthly)" value={fmt.money(deal.monthly_rent, { cents: true })} indent />
            <Row label="NOI (monthly)" value={fmt.money(deal.noi_monthly, { cents: true })} indent />
            <Row label="NOI (annual)" value={fmt.money(deal.noi_annual, { cents: true })} indent />
            <Row label="New debt service (monthly)" value={fmt.money(deal.monthly_debt_service, { cents: true })} indent />
            <Row
              label="Cash flow (monthly)"
              value={fmt.money(deal.cash_flow_monthly, { cents: true })}
              indent
              bold
              tone={cfTone}
            />
            <Row label="DSCR" value={deal.dscr != null ? deal.dscr.toFixed(2) : "n/a"} indent />
            <Row label="ARV uplift" value={deal.arv_uplift_pct != null ? `${deal.arv_uplift_pct.toFixed(1)}%` : "—"} indent />
          </tbody>
        </table>
      </div>
    </div>
  );
}
